import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faFileInvoiceDollar,
  faClipboardCheck,
  faStamp,
  faCircleInfo,
  faEye,
  faLock,
  faLockOpen,
  faCheck,
  faXmark,
  faPenToSquare,
  faChevronRight,
  faScrewdriverWrench,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { customerJobService } from '@/services/customer-job.service';
import { customerAuthorizationService } from '@/services/customer-authorization.service';
import { customerQuotationService } from '@/services/customer-quotation.service';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import { toast } from '@/lib/swal';
import { isNotFoundError } from '@/lib/is-not-found-error';
import { isForbiddenError } from '@/lib/is-forbidden-error';
import {
  CUSTOMER_PORTAL_ROUTES,
  customerEquipmentDetailPath,
} from '@/routes/customer-portal-routes';
import type { CustomerJobDetail } from '@/types/customer-job.types';
import { CustomerSectionCard } from '@/components/customer-portal/CustomerSectionCard';
import { CustomerStatusBadge } from '@/components/customer-portal/CustomerStatusBadge';
import type { CustomerBadgeTone } from '@/components/customer-portal/CustomerStatusBadge';
import { CustomerStatusBanner } from '@/components/customer-portal/CustomerStatusBanner';
import type { CustomerBannerTone } from '@/components/customer-portal/CustomerStatusBanner';
import { CustomerDocumentCard } from '@/components/customer-portal/CustomerDocumentCard';
import { CustomerKeyValue } from '@/components/customer-portal/CustomerKeyValue';
import { CustomerButton } from '@/components/customer-portal/CustomerButton';
import { CustomerErrorState } from '@/components/customer-portal/CustomerErrorState';
import { CustomerUnavailableState } from '@/components/customer-portal/CustomerUnavailableState';
import { CustomerSkeletonDetail } from '@/components/customer-portal/skeletons/CustomerSkeleton';
import { JOB_STATUS_TONE } from './customer-job-status-tone';
import { AUTHORIZATION_STATUS_TONE } from './customer-authorization-status-tone';
import { CustomerAuthorizationCaseDetail } from './CustomerAuthorizationCaseDetail';
import { CustomerReportSection } from './CustomerReportSection';
import {
  CustomerWorkOrderApprovalForm,
  type CustomerWorkOrderApprovalPayload,
} from './CustomerWorkOrderApprovalForm';
// D-131 M7 — the customer's modification request: status, question/reply,
// add-to-request, quotations sent, history; and the round-bound decision.
import { customerModificationService } from '@/services/customer-modification.service';
import type { CustomerModificationRound } from '@/types/customer-modification.types';
import { CustomerModificationSection } from './CustomerModificationSection';

type QuotationAction = 'approve' | 'reject' | 'request-modification' | null;

const CUSTOMER_QUOTATION_RESPOND = 'customer-portal:quotations:respond';
const CUSTOMER_AUTHORIZATION_RESPOND = 'customer-portal:authorizations:respond';

const WARRANTY_LABEL: Record<CustomerJobDetail['warrantyStatus'], string> = {
  UNDER_WARRANTY: 'Under Warranty',
  OUT_OF_WARRANTY: 'Out of Warranty',
  NO_WARRANTY: 'No Warranty',
  PENDING_VERIFICATION: 'Warranty Pending Verification',
};

const WARRANTY_TONE: Record<CustomerJobDetail['warrantyStatus'], CustomerBadgeTone> = {
  UNDER_WARRANTY: 'success',
  OUT_OF_WARRANTY: 'neutral',
  NO_WARRANTY: 'neutral',
  PENDING_VERIFICATION: 'warning',
};

const QUOTATION_CONSEQUENCE: Record<Exclude<QuotationAction, null>, string> = {
  approve:
    'You are confirming acceptance of this quotation. Your Work Order number, description and PDF are required; the service team will be notified.',
  reject:
    'You are declining this quotation. Please provide a reason so the service team can review your response.',
  'request-modification':
    'You are requesting changes to this quotation. Describe what you would like the service team to review.',
};

/* The three decisions, described so the customer knows what each does before
   choosing — order and behaviour are unchanged from the previous UI. */
const DECISIONS: Array<{
  action: Exclude<QuotationAction, null>;
  label: string;
  description: string;
  icon: IconDefinition;
  selected: string;
  iconSelected: string;
}> = [
  {
    action: 'approve',
    label: 'Approve',
    description: 'Accept the quotation and submit your Work Order.',
    icon: faCheck,
    selected: 'border-customer-success bg-customer-success/[0.08] text-customer-success-text',
    iconSelected: 'bg-customer-success/15',
  },
  {
    action: 'reject',
    label: 'Reject',
    description: 'Decline the quotation and tell us why.',
    icon: faXmark,
    selected: 'border-customer-danger bg-customer-danger/[0.06] text-customer-danger-text',
    iconSelected: 'bg-customer-danger/10',
  },
  {
    action: 'request-modification',
    label: 'Request Modification',
    description: 'Ask the service team to revise the quotation.',
    icon: faPenToSquare,
    selected: 'border-customer-primary bg-customer-primary/[0.08] text-customer-primary-text',
    iconSelected: 'bg-customer-primary/15',
  },
];

function fmtDate(value: string | null): string | null {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Real, state-derived "what happens next" — never a fabricated SLA/completion promise. */
function nextStepsText(
  data: CustomerJobDetail,
  canRespondQuotation: boolean,
  round: CustomerModificationRound | null,
): string {
  // D-131 M9 — an open modification request is read from the round, not from
  // the legacy reason column (design §9.2): who owes the next act is exactly
  // what the round's customer status says.
  if (round?.status === 'INFORMATION_NEEDED') {
    return canRespondQuotation
      ? 'The service team has a question about your request. Reply below and they will carry on from there.'
      : 'The service team has a question about your request. An account owner or manager at your company can reply.';
  }
  if (round?.status === 'UNDER_REVIEW') {
    return 'Your modification request has been recorded. The service team will review it and send an updated quotation.';
  }
  if (round?.status === 'QUOTATION_READY') {
    return canRespondQuotation
      ? 'Your decision is needed on the updated quotation below. Once you respond, our team will be notified and will follow up on next steps.'
      : 'The updated quotation is awaiting a decision from an account owner or manager at your company. No action is needed from you.';
  }
  if (data.actionRequired) {
    return canRespondQuotation
      ? 'Your decision is needed on the quotation below. Once you respond, our team will be notified and will follow up on next steps.'
      : 'This quotation is awaiting a decision from an account owner or manager at your company. No action is needed from you.';
  }
  if (data.customerModificationReason) {
    return 'Your modification request has been recorded. The service team will review it and send an updated quotation.';
  }
  // D-129 — the approval carried a Work Order; Commercial must accept it
  if (data.workOrderReviewStatus === 'PENDING_COMMERCIAL_REVIEW') {
    return 'Your approval and Work Order have been received. Our Commercial team is reviewing the Work Order and will confirm acceptance.';
  }
  if (data.workOrderReviewStatus === 'ACCEPTED') {
    return 'Your Work Order has been accepted. The service team will proceed with the work.';
  }
  if (data.customerApprovalRespondedAt) {
    return 'Your response has been recorded. Our team will follow up on next steps.';
  }
  if (data.customerFacingStatus === 'Completed')
    return 'This job is complete. No further action is required.';
  if (data.customerFacingStatus === 'Cancelled')
    return 'This job was cancelled. No further action is required.';
  return 'No action is required from you right now — we will show it here if that changes.';
}

/** The one-line status strip under the job identity: state + what is expected. */
function statusContext(
  data: CustomerJobDetail,
  canRespondQuotation: boolean,
  round: CustomerModificationRound | null,
): { tone: CustomerBannerTone; title: string; message: string } {
  // D-131 M9 — the open request's own customer status leads (design §9.2).
  if (round?.status === 'INFORMATION_NEEDED') {
    return {
      tone: 'warning',
      title: 'We need a little more information',
      message: canRespondQuotation
        ? 'The service team has a question about your request. Your reply goes straight back to them.'
        : 'The service team has a question about your request. An account owner or manager can reply.',
    };
  }
  if (round?.status === 'UNDER_REVIEW') {
    return {
      tone: 'pending',
      title: 'Modification requested',
      message: 'The service team is reviewing your request and will send an updated quotation.',
    };
  }
  if (round?.status === 'QUOTATION_READY') {
    return canRespondQuotation
      ? {
          tone: 'warning',
          title: 'Your decision is needed',
          message:
            'An updated quotation is waiting for your approval, rejection or modification request.',
        }
      : {
          tone: 'info',
          title: 'Awaiting a decision at your company',
          message: 'An account owner or manager needs to respond to the updated quotation.',
        };
  }
  if (data.actionRequired && canRespondQuotation) {
    return {
      tone: 'warning',
      title: 'Your decision is needed',
      message: 'A quotation is waiting for your approval, rejection or modification request.',
    };
  }
  if (data.actionRequired) {
    return {
      tone: 'info',
      title: 'Awaiting a decision at your company',
      message: 'An account owner or manager needs to respond to the quotation.',
    };
  }
  if (data.workOrderReviewStatus === 'PENDING_COMMERCIAL_REVIEW') {
    return {
      tone: 'pending',
      title: 'Work Order under review',
      message: 'Your approval and Work Order were received and are being reviewed by our team.',
    };
  }
  if (data.workOrderReviewStatus === 'ACCEPTED') {
    return {
      tone: 'success',
      title: 'Work Order accepted',
      message: 'The service team will proceed with the work as quoted.',
    };
  }
  if (data.customerModificationReason) {
    return {
      tone: 'pending',
      title: 'Modification requested',
      message: 'The service team is reviewing your request and will send an updated quotation.',
    };
  }
  if (data.customerFacingStatus === 'Declined') {
    return {
      tone: 'danger',
      title: 'Quotation declined',
      message: 'Your response has been recorded. Our team will follow up on next steps.',
    };
  }
  if (data.customerFacingStatus === 'Completed') {
    return { tone: 'success', title: 'Job complete', message: 'No further action is required.' };
  }
  if (data.customerFacingStatus === 'Cancelled') {
    return { tone: 'danger', title: 'Job cancelled', message: 'No further action is required.' };
  }
  if (data.customerFacingStatus === 'Service In Progress') {
    return {
      tone: 'info',
      title: 'Service in progress',
      message: 'Our engineers are working on your equipment. Nothing is needed from you.',
    };
  }
  return {
    tone: 'info',
    title: data.customerFacingStatus,
    message: 'No action is required from you right now.',
  };
}

/** Decision chip shown on the quotation document card. */
function decisionBadge(data: CustomerJobDetail): { label: string; tone: CustomerBadgeTone } | null {
  if (data.actionRequired) return { label: 'Decision Required', tone: 'warning' };
  if (data.customerFacingStatus === 'Approved') return { label: 'Approved', tone: 'success' };
  if (data.customerFacingStatus === 'Declined') return { label: 'Declined', tone: 'danger' };
  if (data.customerFacingStatus === 'Modification Requested')
    return { label: 'Modification Requested', tone: 'info' };
  return null;
}

export function CustomerJobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useCustomerAuthStore((s) => s.user);
  const canRespondQuotation = !!user?.permissions?.includes(CUSTOMER_QUOTATION_RESPOND);
  const canRespondAuthorization = !!user?.permissions?.includes(CUSTOMER_AUTHORIZATION_RESPOND);

  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [quotationAction, setQuotationAction] = useState<QuotationAction>(null);
  const [quotationReason, setQuotationReason] = useState('');
  const [quotationConfirming, setQuotationConfirming] = useState(false);
  const [quotationSubmitting, setQuotationSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['customer-portal', 'jobs', jobId],
    queryFn: () => customerJobService.getById(jobId as string),
    enabled: !!jobId,
  });

  const { data: authorizationCases } = useQuery({
    queryKey: ['customer-portal', 'authorization-cases', jobId],
    queryFn: () => customerAuthorizationService.list({ jobId: jobId as string }),
    enabled: !!jobId,
  });

  // D-131 M7 — the modification request (if any), its history and the sent
  // quotations. Read only once the job itself is visible: the same D-130 scope
  // applies server-side, so an out-of-scope job never reaches these reads.
  const { data: currentRound } = useQuery({
    queryKey: ['customer-portal', 'modification-rounds', jobId, 'current'],
    queryFn: () => customerModificationService.current(jobId as string),
    enabled: !!jobId && !!data,
  });
  const { data: roundHistory } = useQuery({
    queryKey: ['customer-portal', 'modification-rounds', jobId, 'history'],
    queryFn: () => customerModificationService.history(jobId as string),
    enabled: !!jobId && !!data,
  });
  const { data: revisionSummaries } = useQuery({
    queryKey: ['customer-portal', 'quotation-revisions', jobId],
    queryFn: () => customerModificationService.revisions(jobId as string),
    enabled: !!jobId && !!data,
  });
  const [roundSubmitting, setRoundSubmitting] = useState(false);

  const invalidateRoundQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: ['customer-portal', 'jobs', jobId] });
    await queryClient.invalidateQueries({
      queryKey: ['customer-portal', 'modification-rounds', jobId],
    });
    await queryClient.invalidateQueries({
      queryKey: ['customer-portal', 'quotation-revisions', jobId],
    });
  };

  /** T7 / T17 — a message on the open request. */
  async function handleRoundMessage(kind: 'reply' | 'add', roundId: string, message: string) {
    if (!jobId) return;
    setRoundSubmitting(true);
    try {
      if (kind === 'reply') await customerModificationService.reply(jobId, roundId, message);
      else await customerModificationService.addToRequest(jobId, roundId, message);
      toast.success(kind === 'reply' ? 'Your reply has been sent.' : 'Added to your request.');
      await invalidateRoundQueries();
    } catch {
      toast.error(
        'This request may have moved on, or something went wrong. Please refresh and try again.',
      );
      await invalidateRoundQueries();
    } finally {
      setRoundSubmitting(false);
    }
  }

  /**
   * The decision binds to the revised quotation the customer is looking at
   * whenever the open request is waiting on their decision (design §17); the
   * legacy job-level routes remain for a job with no request in progress.
   */
  const boundRound =
    currentRound && currentRound.status === 'QUOTATION_READY' && currentRound.currentRevisionId
      ? { roundId: currentRound.id, revisionId: currentRound.currentRevisionId }
      : null;

  const quotationReasonRequired = quotationAction !== null && quotationAction !== 'approve';
  const hasQuotation = !!(
    data?.quotationReference ||
    data?.quotationDocumentName ||
    data?.hasQuotationDocument
  );

  async function handleQuotationSubmit(approval?: CustomerWorkOrderApprovalPayload) {
    if (!jobId || !quotationAction) return;
    if (quotationReasonRequired && !quotationReason.trim()) {
      toast.error('Please add a reason before submitting.');
      return;
    }
    // D-129 — an approval is only submittable with the complete Work Order.
    if (quotationAction === 'approve' && !approval) {
      toast.error('Please provide the Work Order number, description and PDF to approve.');
      return;
    }
    setQuotationSubmitting(true);
    try {
      if (boundRound) {
        // D-131 M7 — the decision is bound to the revised quotation on screen.
        // The server refuses it (409) if a newer quotation has been sent since.
        if (quotationAction === 'approve') {
          await customerModificationService.decide(jobId, boundRound.roundId, {
            decision: 'APPROVE',
            revisionId: boundRound.revisionId,
            workOrderNumber: approval!.workOrderNumber,
            workOrderDescription: approval!.workOrderDescription,
            file: approval!.file,
          });
        } else if (quotationAction === 'reject') {
          await customerModificationService.decide(jobId, boundRound.roundId, {
            decision: 'REJECT',
            revisionId: boundRound.revisionId,
            message: quotationReason.trim(),
          });
        } else {
          await customerModificationService.decide(jobId, boundRound.roundId, {
            decision: 'REQUEST_MODIFICATION',
            revisionId: boundRound.revisionId,
            requestText: quotationReason.trim(),
          });
        }
      } else if (quotationAction === 'approve') {
        await customerQuotationService.approve(jobId, approval!);
      } else if (quotationAction === 'reject') {
        await customerQuotationService.reject(jobId, { reason: quotationReason.trim() });
      } else {
        await customerQuotationService.requestModification(jobId, {
          reason: quotationReason.trim(),
        });
      }
      toast.success('Your response has been submitted.');
      await invalidateRoundQueries();
      setQuotationAction(null);
      setQuotationReason('');
      setQuotationConfirming(false);
    } catch (err) {
      const status = (err as { response?: { status?: number } } | undefined)?.response?.status;
      toast.error(
        status === 409
          ? 'An updated quotation is now available, or this quotation has already been answered. The latest version is shown now.'
          : 'This quotation may already be resolved, or something went wrong. Please refresh and try again.',
      );
      await refetch();
      await invalidateRoundQueries();
    } finally {
      setQuotationSubmitting(false);
    }
  }

  async function handleDownload() {
    if (!jobId || !data) return;
    setDownloading(true);
    try {
      await customerQuotationService.download(
        jobId,
        data.quotationDocumentName ?? 'quotation-document',
      );
    } catch {
      toast.error("We couldn't download this document. Please try again.");
    } finally {
      setDownloading(false);
    }
  }

  /** The single way out of every dead end on this route. */
  const backToJobs = (
    <CustomerButton
      variant="outline"
      icon={faArrowLeft}
      onClick={() => navigate(CUSTOMER_PORTAL_ROUTES.JOBS)}
    >
      Back to Jobs
    </CustomerButton>
  );

  const context = data ? statusContext(data, canRespondQuotation, currentRound ?? null) : null;
  const decision = data ? decisionBadge(data) : null;

  return (
    <div className="space-y-4 sm:space-y-5">
      <button
        onClick={() => navigate(CUSTOMER_PORTAL_ROUTES.JOBS)}
        className="customer-pressable -ml-2 inline-flex min-h-11 items-center gap-1.5 rounded-customer-sm px-2 text-sm font-medium text-customer-text-secondary hover:bg-customer-text/[0.05] hover:text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" aria-hidden="true" />
        Back to Jobs
      </button>

      {isLoading && <CustomerSkeletonDetail />}

      {/* A 404 (record absent OR outside this contact's D-130 location scope —
             the server answers both identically) and a 403 are settled answers.
             Only a genuine failure gets a retry. */}
      {isError && !isLoading && isNotFoundError(error) && (
        <CustomerUnavailableState
          title="Job not available"
          description="This job either does not exist or is not available to your account."
          action={backToJobs}
        />
      )}

      {isError && !isLoading && !isNotFoundError(error) && isForbiddenError(error) && (
        <CustomerUnavailableState
          icon={faLock}
          title="You do not have access to this job"
          description="Your account does not have permission to view this job. Contact your administrator if you think this is wrong."
          action={backToJobs}
        />
      )}

      {isError && !isLoading && !isNotFoundError(error) && !isForbiddenError(error) && (
        <CustomerErrorState
          message="We couldn't load this job. Please check your connection and try again."
          onRetry={() => void refetch()}
        />
      )}

      {data && !isLoading && !isError && context && (
        <div className="customer-fade-in space-y-4 sm:space-y-5">
          {/* 1. Job identity ─────────────────────────────────────────── */}
          <CustomerSectionCard>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-customer-text-secondary">
                  Service job
                  {data.receivedAt && (
                    <span className="font-medium normal-case tracking-normal">
                      {' '}
                      · Received {fmtDate(data.receivedAt)}
                    </span>
                  )}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-semibold leading-none tracking-tight text-customer-text">
                    {data.inspectionNumber}
                  </h1>
                  <CustomerStatusBadge
                    label={data.customerFacingStatus}
                    tone={JOB_STATUS_TONE[data.customerFacingStatus]}
                    icon
                    size="md"
                  />
                </div>
                <button
                  onClick={() => navigate(customerEquipmentDetailPath(data.equipmentId))}
                  className="customer-pressable mt-3 -ml-1 inline-flex min-h-11 max-w-full items-center gap-2 rounded-customer-sm px-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
                >
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-customer-sm bg-customer-primary/10 text-customer-primary-text"
                    aria-hidden="true"
                  >
                    <FontAwesomeIcon icon={faScrewdriverWrench} className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-customer-primary-text">
                      {data.equipmentName}
                    </span>
                    <span className="block truncate text-xs text-customer-text-secondary">
                      {data.equipmentManufacturerName} · {data.equipmentModelName ?? 'Model N/A'}
                    </span>
                  </span>
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className="h-3 w-3 shrink-0 text-customer-text-secondary"
                    aria-hidden="true"
                  />
                </button>
                <p className="mt-1 text-xs text-customer-text-secondary">
                  Asset {data.equipmentAssetNumber} · Serial {data.equipmentSerialNumber}
                </p>
              </div>
            </div>
          </CustomerSectionCard>

          {/* 2. Status / action context ─────────────────────────────── */}
          <CustomerStatusBanner
            tone={context.tone}
            title={context.title}
            message={context.message}
            data-testid="customer-job-status-context"
          />

          {/* 3. Service summary ──────────────────────────────────────── */}
          <CustomerSectionCard title="Service Summary" icon={faClipboardCheck}>
            <div className="grid gap-5 lg:grid-cols-2">
              <div className="space-y-3">
                {data.customerRequestedScope && (
                  <div>
                    <p className="text-xs font-medium text-customer-text-secondary">Your Request</p>
                    <p className="mt-0.5 text-sm leading-5 text-customer-text">
                      {data.customerRequestedScope}
                    </p>
                  </div>
                )}
                {data.notes && (
                  <div>
                    <p className="text-xs font-medium text-customer-text-secondary">Notes</p>
                    <p className="mt-0.5 text-sm leading-5 text-customer-text">{data.notes}</p>
                  </div>
                )}
                {!data.customerRequestedScope && !data.notes && (
                  <p className="text-sm text-customer-text-secondary">
                    No additional details recorded.
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <CustomerStatusBadge
                    label={WARRANTY_LABEL[data.warrantyStatus]}
                    tone={WARRANTY_TONE[data.warrantyStatus]}
                  />
                  {data.serviceTypeName && (
                    <CustomerStatusBadge label={data.serviceTypeName} tone="neutral" />
                  )}
                  {data.priorityName && (
                    <CustomerStatusBadge label={data.priorityName} tone="neutral" />
                  )}
                </div>
              </div>
              <div className="rounded-customer bg-customer-surface-muted/70 p-3.5 sm:p-4">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-customer-text-secondary">
                  Important dates
                </p>
                <CustomerKeyValue
                  items={[
                    { label: 'Received on', value: fmtDate(data.receivedAt), hideWhenEmpty: true },
                    {
                      label: 'Scheduled for',
                      value: fmtDate(data.scheduledAt),
                      hideWhenEmpty: true,
                    },
                    {
                      label: 'Service started',
                      value: fmtDate(data.startedAt),
                      hideWhenEmpty: true,
                    },
                    {
                      label: 'Service completed',
                      value: fmtDate(data.completedAt),
                      hideWhenEmpty: true,
                    },
                    {
                      label: 'Your response recorded',
                      value: fmtDate(data.customerApprovalRespondedAt),
                      hideWhenEmpty: true,
                    },
                  ]}
                />
              </div>
            </div>
          </CustomerSectionCard>

          {/* 4. Inspection report — read-only; visible only when the backend returns an approved & published report */}
          <CustomerReportSection jobId={jobId as string} />

          {/* 5. Authorization requests — kept structurally independent of the quotation decision below */}
          {authorizationCases && authorizationCases.items.length > 0 && (
            <CustomerSectionCard
              title="Authorization Requests"
              icon={faStamp}
              description="Approvals the service team has asked you for on this job."
              flush
            >
              <ul className="divide-y divide-customer-border">
                {authorizationCases.items.map((c) => {
                  const canAct = c.actionRequired && canRespondAuthorization;
                  return (
                    <li
                      key={c.id}
                      className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-customer-text">
                          {c.caseNumber}
                        </p>
                        {(c.approvalRequestTypeName || c.customApprovalName) && (
                          <p className="truncate text-xs text-customer-text-secondary">
                            {c.approvalRequestTypeName ?? c.customApprovalName}
                          </p>
                        )}
                        <CustomerStatusBadge
                          label={c.status}
                          tone={AUTHORIZATION_STATUS_TONE[c.status]}
                          className="mt-1.5"
                        />
                      </div>
                      <CustomerButton
                        variant={canAct ? 'primary' : 'outline'}
                        size="sm"
                        icon={canAct ? faLockOpen : faEye}
                        onClick={() => setSelectedCaseId(c.id)}
                        className="shrink-0"
                      >
                        {canAct ? 'Respond' : 'View'}
                      </CustomerButton>
                    </li>
                  );
                })}
              </ul>
            </CustomerSectionCard>
          )}

          {/* 6 + 7. Quotation document and customer decision ─────────── */}
          {hasQuotation && (
            <CustomerSectionCard
              title="Quotation"
              icon={faFileInvoiceDollar}
              description={
                data.actionRequired
                  ? 'Review the document, then record your decision below.'
                  : 'The quotation the service team sent for this job.'
              }
              tone={data.actionRequired ? 'warning' : 'default'}
              data-testid="customer-quotation-section"
            >
              <div className="space-y-4">
                <CustomerDocumentCard
                  kind="Quotation"
                  reference={data.quotationReference}
                  fileName={data.quotationDocumentName}
                  meta={data.hasQuotationDocument ? ['PDF document'] : []}
                  available={data.hasQuotationDocument}
                  unavailableText="No downloadable quotation document is available for this job yet."
                  onDownload={() => void handleDownload()}
                  downloading={downloading}
                  downloadLabel="Download Quotation"
                  status={
                    decision ? (
                      <CustomerStatusBadge label={decision.label} tone={decision.tone} icon />
                    ) : undefined
                  }
                >
                  <p className="text-xs leading-4 text-customer-text-secondary">
                    Detailed pricing (amount, tax, and total) is provided in the quotation document
                    — itemized cost data is not yet available as structured information in the
                    portal.
                  </p>
                </CustomerDocumentCard>

                {/* D-131 M7 — the modification request, question/reply, add-to-request,
                    quotations sent and history. Renders nothing for a job with no request. */}
                <CustomerModificationSection
                  current={currentRound ?? null}
                  history={roundHistory ?? []}
                  revisions={revisionSummaries ?? []}
                  canRespond={canRespondQuotation}
                  submitting={roundSubmitting}
                  onReply={(roundId, message) => void handleRoundMessage('reply', roundId, message)}
                  onAddToRequest={(roundId, message) =>
                    void handleRoundMessage('add', roundId, message)
                  }
                />

                {data.customerModificationReason && !currentRound && (
                  <div className="rounded-customer border border-customer-border p-3.5">
                    <p className="text-xs font-medium text-customer-text-secondary">
                      Your Requested Modification
                    </p>
                    <p className="mt-1 text-sm leading-5 text-customer-text">
                      {data.customerModificationReason}
                    </p>
                  </div>
                )}

                {/* D-129 — the Work Order submitted with the approval, and Commercial's acceptance */}
                {data.workOrderNumber && (
                  <CustomerDocumentCard
                    kind="Your Work Order"
                    reference={data.workOrderNumber}
                    fileName={data.workOrderDocumentName}
                    available={!!data.workOrderDocumentName}
                    unavailableText="No Work Order document is attached."
                    meta={data.workOrderDocumentName ? ['Attached with your approval'] : []}
                    status={
                      <CustomerStatusBadge
                        label={
                          data.workOrderReviewStatus === 'ACCEPTED'
                            ? 'Accepted'
                            : 'Awaiting acceptance'
                        }
                        tone={data.workOrderReviewStatus === 'ACCEPTED' ? 'success' : 'info'}
                        icon
                      />
                    }
                    data-testid="customer-work-order"
                  >
                    {data.workOrderDescription && (
                      <p className="text-sm leading-5 text-customer-text">
                        {data.workOrderDescription}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-customer-text-secondary">
                      {data.workOrderReviewStatus === 'ACCEPTED'
                        ? 'Accepted by our Commercial team.'
                        : 'Awaiting acceptance by our Commercial team.'}
                    </p>
                  </CustomerDocumentCard>
                )}

                {data.actionRequired && !canRespondQuotation && (
                  <CustomerStatusBanner
                    tone="info"
                    title="Awaiting a decision from an account owner or manager"
                    message="Your role does not include quotation approval."
                  />
                )}

                {data.actionRequired && canRespondQuotation && (
                  <div className="border-t border-customer-border pt-4">
                    <p className="text-sm font-semibold text-customer-text">Your decision</p>
                    <p className="mt-0.5 text-xs text-customer-text-secondary">
                      Choose one option. You will confirm before anything is sent.
                    </p>
                    <div
                      role="radiogroup"
                      aria-label="Quotation decision"
                      className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3"
                    >
                      {DECISIONS.map((d) => {
                        const active = quotationAction === d.action;
                        return (
                          <button
                            key={d.action}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            onClick={() => {
                              setQuotationAction(d.action);
                              setQuotationConfirming(false);
                            }}
                            className={`customer-pressable flex min-h-14 items-center gap-3 rounded-customer border px-3.5 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring focus-visible:ring-offset-2 focus-visible:ring-offset-customer-surface sm:min-h-[4.5rem] sm:flex-col sm:items-start sm:gap-1.5 ${
                              active
                                ? d.selected
                                : 'border-customer-border bg-customer-surface text-customer-text hover:border-customer-text/30 hover:bg-customer-surface-muted'
                            }`}
                          >
                            <span
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                                active ? d.iconSelected : 'bg-customer-text/[0.06]'
                              }`}
                              aria-hidden="true"
                            >
                              <FontAwesomeIcon icon={d.icon} className="h-3.5 w-3.5" />
                            </span>
                            <span className="min-w-0">
                              <span className="block text-sm font-semibold">{d.label}</span>
                              <span
                                className={`block text-xs leading-4 ${
                                  active ? 'opacity-90' : 'text-customer-text-secondary'
                                }`}
                              >
                                {d.description}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {/* D-129 — Approve opens the Work Order form: number, description and PDF are all required */}
                    {quotationAction === 'approve' && (
                      <CustomerWorkOrderApprovalForm
                        submitting={quotationSubmitting}
                        onCancel={() => setQuotationAction(null)}
                        onSubmit={(payload) => void handleQuotationSubmit(payload)}
                      />
                    )}
                    {quotationAction && quotationAction !== 'approve' && (
                      <div className="customer-slide-up mt-4 space-y-3 rounded-customer border border-customer-border bg-customer-surface-muted/60 p-4 sm:p-5">
                        <p className="flex items-start gap-2 text-sm leading-5 text-customer-text/80">
                          <FontAwesomeIcon
                            icon={faCircleInfo}
                            className="mt-1 h-3.5 w-3.5 shrink-0 text-customer-text-secondary"
                            aria-hidden="true"
                          />
                          {QUOTATION_CONSEQUENCE[quotationAction]}
                        </p>
                        <div>
                          <label
                            htmlFor="quotation-reason"
                            className="block text-sm font-medium text-customer-text"
                          >
                            {quotationAction === 'reject' ? 'Reason' : 'What should change'}
                            {quotationReasonRequired && (
                              <span className="ml-0.5 text-customer-danger-text" aria-hidden="true">
                                *
                              </span>
                            )}
                          </label>
                          <textarea
                            id="quotation-reason"
                            value={quotationReason}
                            onChange={(e) => setQuotationReason(e.target.value)}
                            placeholder={
                              quotationReasonRequired
                                ? 'Please explain (required)'
                                : 'Add a comment (optional)'
                            }
                            rows={3}
                            required={quotationReasonRequired}
                            aria-required={quotationReasonRequired || undefined}
                            className="mt-1.5 block min-h-[6rem] w-full resize-y rounded-customer-sm border border-customer-border bg-customer-surface px-3.5 py-2.5 text-sm leading-5 text-customer-text placeholder:text-customer-text-secondary transition-colors hover:border-customer-text/30 focus:border-customer-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring/40"
                          />
                        </div>
                        {!quotationConfirming ? (
                          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <CustomerButton
                              variant="outline"
                              onClick={() => setQuotationAction(null)}
                            >
                              Cancel
                            </CustomerButton>
                            <CustomerButton
                              variant="primary"
                              onClick={() => setQuotationConfirming(true)}
                              disabled={quotationReasonRequired && !quotationReason.trim()}
                            >
                              Continue
                            </CustomerButton>
                          </div>
                        ) : (
                          <div className="customer-modal-in flex flex-col gap-3 rounded-customer border border-customer-warning/40 bg-customer-warning/[0.08] p-3.5 sm:flex-row sm:items-center">
                            <p className="flex-1 text-sm font-medium text-customer-text">
                              Submit this response? This cannot be undone.
                            </p>
                            <div className="flex flex-col-reverse gap-2 sm:flex-row">
                              <CustomerButton
                                variant="outline"
                                onClick={() => setQuotationConfirming(false)}
                                disabled={quotationSubmitting}
                              >
                                Cancel
                              </CustomerButton>
                              <CustomerButton
                                variant="primary"
                                onClick={() => void handleQuotationSubmit()}
                                loading={quotationSubmitting}
                                loadingLabel="Submitting…"
                              >
                                Confirm
                              </CustomerButton>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CustomerSectionCard>
          )}

          {/* 8. Next steps ───────────────────────────────────────────── */}
          <CustomerSectionCard title="What Happens Next" icon={faCircleInfo} variant="flat">
            <p className="text-sm leading-6 text-customer-text/85">
              {nextStepsText(data, canRespondQuotation, currentRound ?? null)}
            </p>
          </CustomerSectionCard>
        </div>
      )}

      {selectedCaseId && jobId && (
        <CustomerAuthorizationCaseDetail
          caseId={selectedCaseId}
          jobId={jobId}
          canRespond={canRespondAuthorization}
          onClose={() => setSelectedCaseId(null)}
        />
      )}
    </div>
  );
}
